import { PICKUPS } from "./entities.js";
import { COMBAT } from "./weapons.js";
/** "" is the implicit STANDARD entry and is not listed here. */
export const MODIFIERS = [
    {
        id: "moon",
        label: "MOON",
        desc: "Low gravity — floaty jumps, long airtime, fights go vertical.",
        color: "#9d7bff",
        voice: "moon",
    },
    {
        id: "blitz",
        label: "BLITZ",
        desc: "Everyone moves 35% faster. No time to think.",
        color: "#ffd23f",
        voice: "blitz",
    },
    {
        id: "jetpacks",
        label: "JETPACKS",
        desc: "Spawn with a jetpack every life — hold Glide in the air.",
        color: "#3fd0ff",
        voice: "jetpack",
    },
    {
        id: "glass",
        label: "GLASS CANNON",
        desc: "40 HP for everyone. First shot usually wins.",
        color: "#ff5470",
        voice: "glass_cannon",
    },
    {
        id: "vampire",
        label: "VAMPIRE",
        desc: "Every kill heals you. Stay aggressive or bleed out.",
        color: "#b0294a",
        voice: "overdrive",
    },
    {
        id: "adrenaline",
        label: "ADRENALINE",
        desc: "Drop below 30% HP and you deal DOUBLE damage until you heal or die — cornered and twice as lethal.",
        color: "#ff7a2f",
        voice: "overcharge",
    },
    {
        id: "secondwind",
        label: "SECOND WIND",
        desc: "Break contact for 4s and your health regenerates. Win the fight, then get out of the line of fire.",
        color: "#4fe0a8",
        voice: "overdrive",
    },
    {
        id: "rubber",
        label: "RUBBER",
        desc: "The whole floor is a trampoline — every fall and jump rebounds you sky-high. Fights go vertical and never stop moving.",
        color: "#ff5db1",
        // no recorded clip; borrow the MOON line — the closest floaty-movement cue.
        voice: "moon",
    },
    {
        id: "longshot",
        label: "LONG SHOT",
        desc: "Guns hit HARDER the farther the shot flies — point-blank barely scratches. Back off, pick your range, and win the spacing.",
        color: "#6ba8ff",
        // no recorded clip; borrow the IMPERION line — the game's long-range
        // precision weapon, the closest thematic cue for a distance-reward mutator.
        voice: "imperion",
    },
];
const BY_ID = new Map(MODIFIERS.map((m) => [m.id, m]));
/** Unknown / "" → null (STANDARD). Never throws: an old client sending a
 *  modifier this build doesn't know just plays standard rules. */
export function modifierDef(id) {
    return (id && BY_ID.get(id)) || null;
}
/** Label for the HUD / lobby, including the STANDARD fallback. */
export function modifierLabel(id) {
    return modifierDef(id)?.label ?? "STANDARD";
}
/** MOON gravity multiplier. Chosen so a standard jump clears ~2.4x its normal
 *  apex without floating so long that gunfights stall. */
export const MOON_GRAVITY_MUL = 0.42;
/** BLITZ ground/air speed multiplier, on top of the powerup's 1.45x. */
export const BLITZ_SPEED_MUL = 1.35;
/** GLASS CANNON spawn health. */
export const GLASS_MAX_HP = 40;
/** VAMPIRE heal on kill, as a FRACTION of the killer's own max HP pool (so it
 *  scales sanely under GLASS CANNON's 40-HP pool instead of over-healing). At
 *  0.6 a kill claws back most of a fight's damage without a guaranteed full
 *  refill — the aggressor still bleeds if they take two duels at once. */
export const VAMPIRE_HEAL_FRAC = 0.6;
/** Gravity for `stepPlayer`. The map-wide ANTIGRAV event OUTRANKS the modifier:
 *  antigrav is a negative multiplier (upward pull) and scaling it by MOON would
 *  silently weaken a pickup the whole lobby can see land. */
export function modGravityMul(modifier, antigravActive) {
    if (antigravActive)
        return PICKUPS.antigrav.gravityMul;
    return modifier === "moon" ? MOON_GRAVITY_MUL : 1;
}
/** Speed multiplier for `stepPlayer`. `speedy` = the SPEED powerup is live. */
export function modSpeedMul(modifier, speedy) {
    return (speedy ? 1.45 : 1) * (modifier === "blitz" ? BLITZ_SPEED_MUL : 1);
}
/** Base spawn/max health before per-run perks (purge VIGOR) are added. */
export function modBaseMaxHp(modifier) {
    return modifier === "glass" ? GLASS_MAX_HP : COMBAT.maxHp;
}
/** Does this modifier hand out a jetpack on every spawn? */
export function modGivesJetpack(modifier) {
    return modifier === "jetpacks";
}
/** RUBBER restitution: the fraction of downward impact speed a landing rebounds
 *  back upward. 0.72 keeps ~half the fall's height per bounce (apex ∝ v², so
 *  0.72² ≈ 0.52), a lively trampoline that still settles within a few bounces
 *  instead of springing forever. Applied INSIDE `stepPlayer`'s landing branch on
 *  BOTH sides — client prediction and server authority pass the identical scalar
 *  (the moon/blitz pattern), so the bounce is bit-exact and reconcile-clean
 *  (movement invariants 3 & 7). Only a genuine fall (impact speed past
 *  `MOVE.bounceMinSpeed`) rebounds, so ordinary walking is untouched. */
export const RUBBER_RESTITUTION = 0.72;
/** Landing rebound restitution for `stepPlayer` (0 = no bounce, the STANDARD
 *  path — byte-identical to the pre-modifier landing). */
export function modBounce(modifier) {
    return modifier === "rubber" ? RUBBER_RESTITUTION : 0;
}
/** HP restored to the killer on a kill under VAMPIRE (0 otherwise). Pass the
 *  killer's own max HP so GLASS CANNON scales the claw-back down instead of
 *  full-healing off a single frag. Server-only — health is authoritative, the
 *  client never predicts it. */
export function modKillHeal(modifier, killerMaxHp) {
    return modifier === "vampire" ? Math.round(killerMaxHp * VAMPIRE_HEAL_FRAC) : 0;
}
/** ADRENALINE HP threshold: at or below this FRACTION of your max HP you go
 *  berserk (double outgoing damage). 0.3 = the last third of the health bar — a
 *  meaningful "cornered" window that still leaves room to actually fight back.
 *  A fraction of the LIVE max HP so it scales correctly under GLASS CANNON's
 *  40-HP pool or purge's VIGOR-boosted pools. */
export const ADRENALINE_HP_FRAC = 0.3;
/** How far ahead ADRENALINE tops up `dmgUntil` each tick. Long enough that the
 *  OVERCHARGE HUD chip reads as a steady state rather than a flickering sub-tick
 *  countdown, and short enough that the berserk state lapses ~1.2 s after you
 *  heal back above the threshold — a small "adrenaline afterglow", not a
 *  lingering buff. */
export const ADRENALINE_REFRESH_MS = 1200;
/**
 * ADRENALINE modifier: every living player at or below `ADRENALINE_HP_FRAC` of
 * their max HP is granted the OVERCHARGE double-damage state, reusing the
 * per-player `dmgUntil` timer exactly like SUDDEN PURGE — ZERO new replicated
 * schema. Symmetric (everyone gets it when hurt) so it reads as tension, not a
 * rubber-band handout: finishing a wounded enemy is genuinely dangerous.
 *
 * Server-only (health is authoritative; the client never predicts it) and safe
 * to run every tick because HP changes constantly. It is idempotent and
 * allocation-free (a plain for-of, no closure): it only TOPS UP `dmgUntil` to
 * `now + ADRENALINE_REFRESH_MS` and never shortens a longer window, so a real
 * OVERCHARGE pickup or SUDDEN PURGE (which set `dmgUntil` far in the future) is
 * preserved untouched. When a player heals back above the threshold the top-up
 * simply stops and the state lapses on its own within `ADRENALINE_REFRESH_MS`.
 *
 * Returns how many players are currently berserk (for HUD / telemetry). No-op
 * (returns 0 without touching anyone) unless the modifier is ADRENALINE.
 */
export function applyAdrenaline(players, modifier, now) {
    if (modifier !== "adrenaline")
        return 0;
    const until = now + ADRENALINE_REFRESH_MS;
    let berserk = 0;
    for (const p of players) {
        if (p.alive && !p.pending && p.maxHp > 0 && p.hp > 0 && p.hp <= p.maxHp * ADRENALINE_HP_FRAC) {
            berserk++;
            if (p.dmgUntil < until)
                p.dmgUntil = until;
        }
    }
    return berserk;
}
/** SECOND WIND — out-of-combat health regeneration (Halo-shield style).
 *
 *  A player who has not taken damage for `SECONDWIND_DELAY_MS` regenerates
 *  `SECONDWIND_STEP_HP` every `SECONDWIND_STEP_MS` up to their max HP. Distinct
 *  from the purge REGENERATOR perk, which is an always-on trickle that keeps
 *  healing mid-fight; SECOND WIND is COMBAT-GATED, so it rewards disengaging and
 *  punishes over-extension without ever helping a player who is still trading.
 *  Symmetric across the lobby (everyone recovers the same way), so it shifts the
 *  feel — win the duel, then break line of sight to recover — without any
 *  rubber-band handout.
 *
 *  Server-only: health is authoritative and the client never predicts it. The
 *  timing state (`lastHurtAt`, the next heal-step deadline) lives on the server
 *  sim, NOT on replicated schema — nothing new goes on the wire.
 */
export const SECONDWIND_DELAY_MS = 4000;
export const SECONDWIND_STEP_MS = 300;
export const SECONDWIND_STEP_HP = 8;
const SW_SCRATCH = { hp: 0, swNextAt: 0, healed: false };
/** Compute one player's SECOND WIND regen for the current tick. Pure: given the
 *  live HP/cap, the wall-clock of the last damage taken, the next-step deadline
 *  and `now`, it returns the (possibly unchanged) HP and deadline. Heals at most
 *  one step per call, so the rate is fixed at `SECONDWIND_STEP_HP` per
 *  `SECONDWIND_STEP_MS` regardless of tick rate. */
export function secondWindTick(hp, maxHp, lastHurtAt, swNextAt, now) {
    SW_SCRATCH.hp = hp;
    SW_SCRATCH.swNextAt = swNextAt;
    SW_SCRATCH.healed = false;
    if (hp > 0 &&
        hp < maxHp &&
        now - lastHurtAt >= SECONDWIND_DELAY_MS &&
        now >= swNextAt) {
        const healed = Math.min(maxHp, hp + SECONDWIND_STEP_HP);
        SW_SCRATCH.hp = healed;
        SW_SCRATCH.swNextAt = now + SECONDWIND_STEP_MS;
        SW_SCRATCH.healed = healed > hp;
    }
    return SW_SCRATCH;
}
//# sourceMappingURL=modifiers.js.map